import React from 'react';
import { getSystemUser } from '@/api/accounts';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardHeader,
  Chip,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import Grid from '@mui/system/Unstable_Grid/Grid';
import { ArrowLeft as ArrowLeftIcon } from '@phosphor-icons/react/dist/ssr/ArrowLeft';
import { User as UserIcon } from '@phosphor-icons/react/dist/ssr/User';
import { Buildings as BuildingsIcon } from '@phosphor-icons/react';
import { HouseSimple as HouseSimpleIcon } from '@phosphor-icons/react';
import { Plus as PlusIcon } from '@phosphor-icons/react';
import { Trash as TrashIcon } from '@phosphor-icons/react';
import { PencilSimple as PencilSimpleIcon } from '@phosphor-icons/react';
import { toTitleCase } from '@/lib/v2/typography/to-title-case';
import { ShieldCheck as ShieldCheckIcon } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import type { User } from '@/types/v2/user';
import { paths } from '@/paths';
import { useDialog } from '@/hooks/v2/use-dialog';
import { useUser } from '@/hooks/v2/use-user';
import { PropertyItem } from '@/components/core/v2/property-item';
import { PropertyList } from '@/components/core/v2/property-list';

import { AddCommunityModal } from '../components/add-community-modal';
import { EditAccountRoleModal } from '../components/edit-account-role-modal';
import { removeUserFromCommunity } from '@/api/accounts';
import { useConfirm } from 'material-ui-confirm';

interface SystemUserApiResponse {
  systemUser: User;
}

export function AccountSystemUserPage(): React.JSX.Element {
  const { userId } = useParams<{ userId: string }>();
  const { account } = useUser();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const communityDialog = useDialog();
  const accountRoleDialog = useDialog();

  const { data, isLoading, error, refetch } = useQuery<SystemUserApiResponse>({
    queryKey: ['system-user', userId],
    queryFn: async () => {
      return await getSystemUser(account!.id, userId!);
    },
  });

  const systemUser = data?.systemUser;

  const handleOnSuccess = React.useCallback(() => {
    toast.success('User updated successfully', {
      style: {
        background: '#107E4A',
        color: '#fff',
        borderColor: '#107E4A',
      },
      description: 'The user has been successfully updated.',
      position: 'top-right',
    });
    refetch();
  }, [refetch]);

  const handleRemoveCommunity = async (communityId: string, communityName: string) => {
    try {
      await confirm({
        title: 'Remove Community',
        description: `Are you sure you want to remove this user from ${communityName}?`,
      });
      
      await removeUserFromCommunity(account!.id, userId!, communityId);
      toast.success('User removed from community');
      refetch();
    } catch (error) {
      if (error) {
        toast.error('Failed to remove user from community');
      }
    }
  };

  return (
    <React.Fragment>
      <Box sx={{ mb: 2 }}>
        <Button
          aria-label="Back to System Users"
          onClick={() => {
            navigate(paths.settings.account.systemUsers.index);
          }}
          startIcon={<ArrowLeftIcon size={24} />}
          variant="text"
        >
          Back to System Users
        </Button>
      </Box>

      {isLoading ? <div>Loading...</div> : null}
      {error ? <div>Error: {error.message}</div> : null}

      <Stack spacing={4}>
        <Grid container spacing={4}>
          {data ? (
            <>
              <Grid lg={12} xs={12}>
                <Card>
                  <CardHeader
                    avatar={
                      <Avatar>
                        <UserIcon fontSize="var(--Icon-fontSize)" />
                      </Avatar>
                    }
                    title="User Details"
                  />
                  <PropertyList
                    divider={<Divider />}
                    orientation="vertical"
                    sx={{ '--PropertyItem-padding': '12px 24px' }}
                  >
                    {(
                      [
                        { key: 'ID', value: <Chip label={data.systemUser.id} size="small" variant="soft" /> },
                        { key: 'Name', value: `${data.systemUser.first_name} ${data.systemUser.last_name}` },
                        { key: 'Email', value: data.systemUser.email },
                        { key: 'Phone', value: data.systemUser.phone },
                        { 
                          key: 'Status', 
                          value: data.systemUser.email_verified_at ? (
                            <Chip label="Verified" size="small" color="success" />
                          ) : (
                            <Chip label="Pending" size="small" color="warning" />
                          )
                        },
                      ] satisfies { key: string; value: React.ReactNode }[]
                    ).map(
                      (item): React.JSX.Element => (
                        <PropertyItem key={item.key} name={item.key} value={item.value} />
                      )
                    )}
                  </PropertyList>
                </Card>
              </Grid>

              <Grid lg={12} xs={12}>
                <Card>
                  <CardHeader
                    avatar={<ShieldCheckIcon size={24} />}
                    title="Account Role"
                    action={
                      <IconButton
                        onClick={accountRoleDialog.handleOpen}
                        size="small"
                      >
                        <PencilSimpleIcon size={18} />
                      </IconButton>
                    }
                  />
                  <Divider />
                  <Box sx={{ p: 2 }}>
                    {systemUser?.account_users && systemUser.account_users.length > 0 ? (
                      <Stack spacing={1}>
                        {systemUser.account_users.map((accountUser: any) => (
                          <Box key={accountUser.id}>
                            {accountUser.role ? (
                              <Chip 
                                label={toTitleCase(accountUser.role.name ?? '')} 
                                size="medium" 
                                color="info" 
                              />
                            ) : (
                              <Typography color="text.secondary" variant="body2">
                                No role assigned
                              </Typography>
                            )}
                          </Box>
                        ))}
                      </Stack>
                    ) : (
                      <Typography color="text.secondary" variant="body2">
                        No account role assigned
                      </Typography>
                    )}
                  </Box>
                </Card>
              </Grid>

              <Grid lg={12} xs={12}>
                <Card>
                  <CardHeader
                    avatar={<BuildingsIcon size={24} />}
                    title="Communities"
                    action={
                      <Button
                        onClick={communityDialog.handleOpen}
                        size="small"
                        startIcon={<PlusIcon size={18} />}
                        variant="outlined"
                      >
                        Add Community
                      </Button>
                    }
                  />
                  <Divider />
                  <Box sx={{ p: 2 }}>
                    {systemUser?.communities && systemUser.communities.length > 0 ? (
                      <Stack spacing={2}>
                        {systemUser.communities.map((community: any) => (
                          <Card key={community.id} variant="outlined">
                            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  {community.name}
                                </Typography>
                                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                  {community.role && (
                                    <Chip 
                                      label={toTitleCase(community.role.name ?? '')} 
                                      size="small" 
                                      color="primary" 
                                      variant="outlined"
                                    />
                                  )}
                                  {community.user_title && (
                                    <Chip 
                                      label={toTitleCase(community.user_title.title ?? '')} 
                                      size="small" 
                                      variant="outlined"
                                    />
                                  )}
                                </Stack>
                              </Box>
                              <IconButton
                                color="error"
                                size="small"
                                onClick={() => handleRemoveCommunity(community.id, community.name)}
                              >
                                <TrashIcon size={18} />
                              </IconButton>
                            </Box>
                          </Card>
                        ))}
                      </Stack>
                    ) : (
                      <Typography color="text.secondary" variant="body2">
                        No communities assigned
                      </Typography>
                    )}
                  </Box>
                </Card>
              </Grid>

              <Grid lg={12} xs={12}>
                <Card>
                  <CardHeader
                    avatar={<HouseSimpleIcon size={24} />}
                    title="Units"
                    subheader="Assign or edit units from the Units page"
                  />
                  <Divider />
                  <Box sx={{ p: 2 }}>
                    {systemUser?.units && systemUser.units.length > 0 ? (
                      <Stack spacing={2}>
                        {systemUser.units.map((unit: any) => (
                          <Card key={unit.id} variant="outlined">
                            <Box sx={{ p: 2 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                Unit {unit.unit_number}
                              </Typography>
                              {unit.community && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                  {unit.community.name}
                                </Typography>
                              )}
                              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                {unit.role && (
                                  <Chip 
                                    label={toTitleCase(unit.role.name ?? '')} 
                                    size="small" 
                                    color="secondary" 
                                    variant="outlined"
                                  />
                                )}
                                {unit.user_title && (
                                  <Chip 
                                    label={toTitleCase(unit.user_title.title ?? '')} 
                                    size="small" 
                                    variant="outlined"
                                  />
                                )}
                              </Stack>
                            </Box>
                          </Card>
                        ))}
                      </Stack>
                    ) : (
                      <Typography color="text.secondary" variant="body2">
                        No units assigned
                      </Typography>
                    )}
                  </Box>
                </Card>
              </Grid>
            </>
          ) : null}
        </Grid>
      </Stack>

      <AddCommunityModal
        accountId={account?.id!}
        userId={userId!}
        existingCommunityIds={systemUser?.communities?.map((c: any) => c.id) || []}
        onClose={communityDialog.handleClose}
        onSuccess={handleOnSuccess}
        open={communityDialog.open}
      />

      <EditAccountRoleModal
        accountId={account?.id!}
        userId={userId!}
        currentRole={systemUser?.account_users?.[0]?.role?.name || ''}
        onClose={accountRoleDialog.handleClose}
        onSuccess={handleOnSuccess}
        open={accountRoleDialog.open}
      />
    </React.Fragment>
  );
}
